export type UrlIssueIndexParams = {
  owner: string
  repo: string
}

export type UrlPlanningIssuesParams = {
  accountType: 'orgs' | 'users'
  account: string
}

export type UrlPlanningViewShowParams = {
  accountType: 'orgs' | 'users'
  account: string
  viewId: string
}

export type IssueSearchTypeVariant = 'ISSUE_HYBRID' | 'ISSUE_ADVANCED'

export const ISSUES_INDEX_CONTENT_PERSISTER_PREFIX = 'repo-issues-dashboard-v1'
export const PLANNING_ISSUES_CONTENT_PERSISTER_PREFIX = 'planning-issues-dashboard-v1'
// Hand-maintained: unlike the proto content it wraps, the saved-view envelope
// (`layout`/`configuration`) has no generated fingerprint. Bump on a breaking
// envelope change so old records cannot be restored.
export const PLANNING_VIEW_SHOW_CONTENT_PERSISTER_PREFIX = 'planning-view-show-v1'
export const PLANNING_ISSUES_DEFAULT_QUERY =
  'is:issue state:open archived:false (author:@me OR assignee:@me) sort:created-desc'

export const normalizeIssuesIndexContentStorageQueryKey = (queryKey: readonly unknown[]) =>
  queryKey.length > 4 ? [queryKey[4]] : undefined

export const getIssuesIndexContentQueryDeps = ({
  pathname,
  query,
  page,
}: {
  pathname: string
  query?: string | null
  page?: string | null
}) => {
  const normalizedQuery = query?.trim()
  const searchParams: Record<string, string> = {page: page || '1'}
  if (normalizedQuery) searchParams.q = normalizedQuery

  return {pathname, searchParams}
}

export const getIssuesIndexContentStorageQueryKey = ({
  owner,
  repo,
  query,
  page,
}: UrlIssueIndexParams & {query?: string | null; page?: string | null}) => [
  getIssuesIndexContentQueryDeps({
    pathname: `/${owner}/${repo}/issues`,
    query,
    page,
  }),
]

export const getDefaultIssuesIndexContentStorageQueryKey = ({owner, repo}: UrlIssueIndexParams) =>
  getIssuesIndexContentStorageQueryKey({owner, repo})

export const getPlanningIssuesContentQueryDeps = ({
  pathname,
  query,
  page,
}: {
  pathname: string
  query?: string | null
  page?: string | null
}) => ({
  pathname,
  searchParams: {
    q: query?.trim() || PLANNING_ISSUES_DEFAULT_QUERY,
    page: page || '1',
  },
})

export const getPlanningIssuesContentStorageQueryKey = ({
  accountType,
  account,
  query,
  page,
}: UrlPlanningIssuesParams & {query?: string | null; page?: string | null}) => [
  getPlanningIssuesContentQueryDeps({
    pathname: `/${accountType}/${account}/planning/issues`,
    query,
    page,
  }),
]

// The single source of truth for a saved view's content search params. The
// route builds its runtime query key from this, and the service worker
// reconstructs the same storage key from a URL alone, so the two cannot drift.
// Unlike the Planning issues list this does not fall back to a default query:
// a saved view's query lives on the view, and `q` only appears while an edited
// query is being previewed.
export const getPlanningViewShowSearchParams = ({
  query,
  page,
  layout,
}: {
  query?: string | null
  page?: string | null
  layout?: string | null
}) => ({
  page: page || '1',
  q: query || '',
  layout: layout || '',
})

export const getPlanningViewShowContentQueryDeps = ({
  pathname,
  query,
  page,
  layout,
}: {
  pathname: string
  query?: string | null
  page?: string | null
  layout?: string | null
}) => ({
  pathname,
  searchParams: getPlanningViewShowSearchParams({query, page, layout}),
})

export const getPlanningViewShowContentStorageQueryKey = ({
  accountType,
  account,
  viewId,
  query,
  page,
  layout,
}: UrlPlanningViewShowParams & {query?: string | null; page?: string | null; layout?: string | null}) => [
  getPlanningViewShowContentQueryDeps({
    pathname: `/${accountType}/${account}/planning/views/${viewId}`,
    query,
    page,
    layout,
  }),
]

// The search type the client requests is resolved by Rails: ISSUE_HYBRID when hybrid/semantic
// search is available (the dotcom default) and ISSUE_ADVANCED otherwise (e.g. single-tenant
// enterprise or when the semantic endpoint is unavailable). Consumers that can't read that value
// (the service worker) default to ISSUE_HYBRID here and try both variants when looking up keys.
// `issues_default_best_match_sort` is assumed fully enabled. `showIssueFieldPills` is always
// `true` since the `issue_fields_index_pills` flag has been graduated.
export const getIssueIndexPageQueryDefaultValues = (
  {owner, repo}: UrlIssueIndexParams,
  issueSearchType: IssueSearchTypeVariant = 'ISSUE_HYBRID',
) => ({
  query: `is:issue state:open repo:${owner}/${repo} sort:relevance-desc`,
  first: 25,
  labelPageSize: 20,
  showIssueFieldPills: true,
  skip: 0,
  type: issueSearchType,
})
